import React, { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import theme from './themes';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import LoadingState from './components/LoadingState';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SiteProvider } from './contexts/SiteContext';
import FeedbackButton from './components/FeedbackButton';
import ImpersonationBanner from './components/ImpersonationBanner';

// Shared pages
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'));
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvitePage'));
const ScorecardPage = lazy(() => import('./pages/ScorecardPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));

// Smart dashboard (handles both all-sites and per-site views)
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const WeeklyReportPage = lazy(() => import('./pages/WeeklyReportPage'));

// Site pages
const DirectivesPage = lazy(() => import('./pages/DirectivesPage'));
const MeasurementsPage = lazy(() => import('./pages/MeasurementsPage'));

// Advanced pages (gated to enterprise/admin)
const PipelinePage = lazy(() => import('./pages/PipelinePage'));
const GeoRulesPage = lazy(() => import('./pages/GeoRulesPage'));

// Feature config pages (gated by tier)
const FeatureTogglesPage = lazy(() => import('./pages/FeatureTogglesPage'));
const ChatConfigPage = lazy(() => import('./pages/ChatConfigPage'));
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage'));
const GuardrailsConfigPage = lazy(() => import('./pages/GuardrailsConfigPage'));
const FeedbackTriagePage = lazy(() => import('./pages/FeedbackTriagePage'));
const AnnouncementsPage = lazy(() => import('./pages/AnnouncementsPage'));
const WebhooksConfigPage = lazy(() => import('./pages/WebhooksConfigPage'));

// Hosting + DNS pages
const SiteHostingPage = lazy(() => import('./pages/SiteHostingPage'));
const DNSSetupPage = lazy(() => import('./pages/DNSSetupPage'));

// Attorney onboarding (public)
const GetStartedPage = lazy(() => import('./pages/GetStartedPage'));

// Shareable public report (no auth)
const PublicReportPage = lazy(() => import('./pages/PublicReportPage'));

// Conversational onboarding setup
const SetupPage = lazy(() => import('./pages/SetupPage'));

// Bottom nav pages
const BillingPage = lazy(() => import('./pages/BillingPage'));

// Admin platform pages (platform admin only)
const AdminOverviewPage = lazy(() => import('./pages/AdminOverviewPage'));
const AllSitesPage = lazy(() => import('./pages/AllSitesPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'));
const CampaignsPage = lazy(() => import('./pages/CampaignsPage'));
const DesignSessionsPage = lazy(() => import('./pages/DesignSessionsPage'));
const ImpersonationPage = lazy(() => import('./pages/ImpersonationPage'));
const RBACPage = lazy(() => import('./pages/RBACPage'));

// Patchwise lead-gen pages
const AnalysesListPage = lazy(() => import('./pages/AnalysesListPage'));
const DemoSitesPage = lazy(() => import('./pages/DemoSitesPage'));
const AdminUsersPage = lazy(() => import('./pages/AdminUsersPage'));
const AdminOrgsPage = lazy(() => import('./pages/AdminOrgsPage'));
const SystemHealthPage = lazy(() => import('./pages/SystemHealthPage'));

// Content editing
const ContentPage = lazy(() => import('./pages/ContentPage'));

// Interaction Map
const InteractionMapPage = lazy(() => import('./pages/InteractionMapPage'));

const PAGE_TITLES: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/weekly-report': 'Weekly Report',
  '/directives': 'Recommendations',
  '/measurements': 'Progress',
  '/pipeline': 'Pipeline',
  '/geo-rules': 'Location Rules',
  '/hosting': 'Hosting & Domain',
  '/dns-setup': 'Custom Domain Setup',
  '/settings': 'Settings',
  '/billing': 'Billing',
  '/feature-toggles': 'Site Features',
  '/chat-config': 'AI Chat',
  '/knowledge-base': 'Chat Training',
  '/guardrails': 'Content Safety',
  '/feedback-triage': 'Customer Feedback',
  '/announcements': 'Announcements',
  '/webhooks': 'Webhooks',
  '/analyses': 'Analyses',
  '/demo-sites': 'Demo Sites',
  '/onboarding': 'Setup',
  '/admin-overview': 'Admin Overview',
  '/all-sites': 'All Sites',
  '/analytics': 'Analytics',
  '/audit-log': 'Audit Log',
  '/campaigns': 'Campaigns',
  '/design-sessions': 'Design Sessions',
  '/impersonation': 'Impersonation',
  '/rbac': 'Roles & Access',
  '/admin-users': 'Users',
  '/admin-orgs': 'Organizations',
  '/system-health': 'System Health',
  '/content': 'Content Editing',
  '/interaction-map': 'Interaction Map',
  '/report': 'Shared Report',
  '/setup': 'Setup',
};

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    const title = PAGE_TITLES[pathname] || 'Patchwise';
    document.title = `${title} - Patchwise`;
  }, [pathname]);
  return null;
};

const DRAWER_WIDTH = 260;

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    const currentPath = window.location.pathname + window.location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(currentPath)}`} replace />;
  }

  return <>{children}</>;
}

function HomeRedirect() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />;
}

function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <ProtectedRoute>
      <SiteProvider>
        <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
          <Sidebar mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
            }}
          >
            <ImpersonationBanner />
            <TopBar onMenuClick={() => setMobileOpen(true)} />
            <Box
              component="main"
              id="main-content"
              sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: 'background.default' }}
            >
              <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
                <ErrorBoundary>
                <Suspense fallback={<LoadingState />}>
                  <Routes>
                    {/* Core routes */}
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/weekly-report" element={<WeeklyReportPage />} />
                    <Route path="/measurements" element={<MeasurementsPage />} />
                    <Route path="/directives" element={<DirectivesPage />} />

                    {/* Hosting routes */}
                    <Route path="/hosting" element={<SiteHostingPage />} />
                    <Route path="/dns-setup" element={<DNSSetupPage />} />

                    {/* Content editing */}
                    <Route path="/content" element={<ContentPage />} />

                    {/* Feature config routes */}
                    <Route path="/feature-toggles" element={<FeatureTogglesPage />} />
                    <Route path="/chat-config" element={<ChatConfigPage />} />
                    <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
                    <Route path="/guardrails" element={<GuardrailsConfigPage />} />
                    <Route path="/feedback-triage" element={<FeedbackTriagePage />} />
                    <Route path="/announcements" element={<AnnouncementsPage />} />
                    <Route path="/webhooks" element={<WebhooksConfigPage />} />

                    {/* Advanced routes */}
                    <Route path="/geo-rules" element={<GeoRulesPage />} />
                    <Route path="/pipeline" element={<PipelinePage />} />
                    <Route path="/interaction-map" element={<InteractionMapPage />} />

                    {/* Bottom nav routes */}
                    <Route path="/billing" element={<BillingPage />} />
                    <Route path="/settings" element={<SettingsPage />} />

                    {/* Admin platform routes */}
                    <Route path="/admin-overview" element={<AdminOverviewPage />} />
                    <Route path="/all-sites" element={<AllSitesPage />} />
                    <Route path="/analytics" element={<AnalyticsPage />} />
                    <Route path="/audit-log" element={<AuditLogPage />} />
                    <Route path="/campaigns" element={<CampaignsPage />} />
                    <Route path="/design-sessions" element={<DesignSessionsPage />} />
                    <Route path="/impersonation" element={<ImpersonationPage />} />
                    <Route path="/rbac" element={<RBACPage />} />

                    {/* Admin management routes */}
                    <Route path="/admin-users" element={<AdminUsersPage />} />
                    <Route path="/admin-orgs" element={<AdminOrgsPage />} />
                    <Route path="/system-health" element={<SystemHealthPage />} />

                    {/* Lead-gen routes */}
                    <Route path="/analyses" element={<AnalysesListPage />} />
                    <Route path="/demo-sites" element={<DemoSitesPage />} />

                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </Suspense>
                </ErrorBoundary>
              </Box>
            </Box>
          </Box>
        </Box>
      </SiteProvider>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <ErrorBoundary>
          <ScrollToTop />
          <Suspense fallback={<LoadingState />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/oauth/callback" element={<OAuthCallback />} />
              <Route path="/accept-invite" element={<AcceptInvitePage />} />
              {/* Public routes */}
              <Route path="/" element={<HomeRedirect />} />
              {/* Landing page removed — Patchwise marketing is at patchwise.ai (Vercel) */}
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/get-started" element={<GetStartedPage />} />
              <Route path="/analyze/:analysisId" element={<ScorecardPage />} />
              <Route path="/report/:shareToken" element={<PublicReportPage />} />
              {/* Protected onboarding wizard */}
              <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
              {/* Conversational setup assistant */}
              <Route path="/setup" element={<ProtectedRoute><SetupPage /></ProtectedRoute>} />
              <Route path="/*" element={<AppLayout />} />
            </Routes>
          </Suspense>
          <FeedbackButton />
          </ErrorBoundary>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
